/**
 * Доменные типы ОСНОВА.Логист (ЭТрН, ТЗ AI-LOGIST-2026). Зеркалят Rust-проекции
 * `osnova_storage::logist_dictionaries` (LogistDriver / LogistRollingStock).
 */

/** Справочники модуля. */
export type LogistDictName = 'drivers' | 'vehicles' | 'trailers';

/** Водитель (§8.2). ПДн — минимизация: только необходимое для ЭТрН. */
export interface LogistDriver {
  id: string;
  full_name: string;
  license_series: string | null;
  license_number: string | null;
  license_valid_until: string | null;
  /**
   * Дата выдачи удостоверения. Формат ЭТрН требует её всегда, когда не указан
   * ИНН водителя; срок действия её не заменяет.
   */
  license_issued_at: string | null;
  inn: string | null;
  phone: string | null;
  carrier_name: string | null;
  operator_driver_id: string | null;
  /** Зарегистрирован в приложении оператора ИС ЭПД (предпосылка Т2). */
  operator_registered: boolean;
  /** Выпущена ПЭП/УНЭП у оператора. */
  has_esignature: boolean;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Подвижной состав — транспортное средство или прицеп (§8.2). Массо-габаритные
 * атрибуты (кг) — вход весового контроля §8.4.
 */
export interface LogistRollingStock {
  id: string;
  plate_number: string;
  brand: string | null;
  sts_number: string | null;
  max_permitted_mass_kg: number | null;
  unladen_mass_kg: number | null;
  load_capacity_kg: number | null;
  owner: string | null;
  is_leased: boolean;
  /**
   * Тип владения по классификатору оператора — реквизит документа. Флаг
   * `is_leased` его не заменяет: он про учёт, а не про накладную.
   */
  ownership_type: LogistOwnershipType | null;
  /** Тип техники: «Самосвал», «Тягач седельный». */
  vehicle_kind: string | null;
  /** Вместимость кузова, м³. */
  body_volume_m3: number | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Типы владения техникой — зеркало классификатора оператора. */
export const LOGIST_OWNERSHIP_TYPES = [
  { code: 'Property', label: 'Собственность' },
  { code: 'Rent', label: 'Аренда' },
  { code: 'Leasing', label: 'Лизинг' },
  { code: 'GratuitousUse', label: 'Безвозмездное пользование' },
  { code: 'JointPropertyOfSpouses', label: 'Совместная собственность супругов' },
] as const;

export type LogistOwnershipType = (typeof LOGIST_OWNERSHIP_TYPES)[number]['code'];

/**
 * Задача ленты — то, что требует внимания человека.
 *
 * Задачи вычисляемые: фоновый обмен пересобирает их на каждом тике. Причина
 * исчезла — задача закрылась сама; создать её руками нельзя.
 */
export interface LogistTask {
  id: string;
  kind: LogistTaskKind;
  /** К чему относится: etrn | trip | inbox. */
  entity_kind: string;
  entity_id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail: string | null;
  state: 'open' | 'acknowledged';
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
}

export type LogistTaskKind =
  | 'submit_failed'
  | 'operator_silent'
  | 'awaiting_signature'
  | 'not_closed'
  | 'overload_hold'
  | 'inbox_quarantine'
  | 'evidence_missing';

/** Короткие названия поводов — для группировки и фильтра. */
export const LOGIST_TASK_KIND_LABELS: Record<LogistTaskKind, string> = {
  submit_failed: 'Не ушло оператору',
  operator_silent: 'Оператор молчит',
  awaiting_signature: 'Ждёт подписи',
  not_closed: 'Не закрыта',
  overload_hold: 'Перегруз',
  inbox_quarantine: 'Входящая без связки',
  evidence_missing: 'Нет архива',
};

/**
 * Доказательный архив ЭТрН (§8.5) — комплект документов оператора, сохранённый
 * в нашем контуре: XML титулов, файлы подписей, печатная форма.
 */
export interface LogistEtrnEvidence {
  id: string;
  etrn_id: string;
  external_id: string;
  operator: string;
  /** Источник выгрузки; сейчас всегда комплект документооборота. */
  source: string;
  content_type: string;
  size_bytes: number;
  /** SHA-256 сохранённых байтов — подтверждение неизменности. */
  sha256: string;
  fetched_at: string;
  fetched_by: string | null;
}

/**
 * Приёмка груза грузополучателем — данные титула Т3 (§8).
 *
 * Второй и последний титул, который выпускается программно: генерация XML у
 * оператора есть только для Т1 и Т3, остальные части документа создаются в его
 * кабинете (ADR-0017 §11).
 */
export interface LogistDeliveryAcceptance {
  /** Полная приёмка, частичная или отказ. */
  operation: 'full' | 'partial' | 'rejection';
  /** Обязательна при отказе — попадёт в юридический документ. */
  rejection_reason?: string | null;
  gross_mass_kg?: number | null;
  net_mass_kg?: number | null;
  places?: number | null;
  cargo_condition?: string | null;
  /**
   * Время работы машины на выгрузке. Формат накладной требует все три отметки:
   * заявленную подачу и фактические прибытие и убытие. Подписант в форму не
   * вводится — он берётся из карточки того, кто оформляет приёмку.
   */
  stated_arrival_at?: string | null;
  arrival_at?: string | null;
  departure_at?: string | null;
  delivery_address?: string | null;
}

/** Виды приёмки простым языком. */
export const LOGIST_DELIVERY_OPERATION_LABELS: Record<string, string> = {
  full: 'Принят полностью',
  partial: 'Принят частично',
  rejection: 'Отказ в приёмке',
};

/**
 * Титул в составе ЭТрН (§8.5/§12.1) — часть документа, которую подписывает
 * своя сторона. Зеркало состояния оператора: тип и статус приходят дословно.
 */
export interface LogistEtrnTitle {
  id: string;
  etrn_id: string;
  external_id: string;
  title_type: string;
  status: string | null;
  created_at_op: string | null;
  signature_rejected: boolean;
  synced_at: string;
}

/**
 * Названия титулов простым языком. Перечень ведёт оператор, поэтому неизвестный
 * тип показываем как есть, а не прячем.
 *
 * Номера Т1–Т4 подтверждены спецификацией оператора; у титулов стоимости,
 * переадресации и эстафеты номер зависит от прочтения 140-ФЗ, поэтому вместо
 * номера — описание того, что произошло.
 */
export const LOGIST_TITLE_TYPE_LABELS: Record<string, string> = {
  ConsignorReceptionTitle: 'Т1 · Груз передан отправителем',
  CarrierReceptionTitle: 'Т2 · Груз принят перевозчиком',
  ConsigneeDeliveryTitle: 'Т3 · Груз получен получателем',
  CarrierDeliveryTitle: 'Т4 · Груз сдан перевозчиком',
  CarrierCostTitle: 'Стоимость перевозки — от перевозчика',
  ConsignorCostTitle: 'Стоимость перевозки — от отправителя',
  CarrierReaddressTitle: 'Переадресация — от перевозчика',
  ConsignorReaddressTitle: 'Переадресация — от отправителя',
  CarrierRelayTitle: 'Замена машины или водителя в пути',
};

/** Статусы титула у оператора. */
export const LOGIST_TITLE_STATUS_LABELS: Record<string, string> = {
  New: 'Ждёт подписи',
  SignatureVerifying: 'Подпись проверяется',
  Signed: 'Подписан',
  InvalidSignature: 'Подпись недействительна',
};

/**
 * Ящик ЭДО контрагента (§8.2, ADR-0017 §10) — соответствие «ИНН → ящик
 * оператора». Формат ЭТрН идентифицирует стороны перевозки именно ящиком.
 */
export interface LogistEdoBox {
  id: string;
  counterparty_id: string | null;
  operator: string;
  inn: string;
  kpp: string | null;
  /** Идентификатор ящика у оператора. НЕ идентификатор участника ЭДО. */
  box_id: string;
  name: string | null;
  /** Идентификатор участника ЭДО в формате ФНС (`2BM-<ИНН>-…`). */
  fns_participant_id: string | null;
  is_roaming: boolean;
  /** Зарегистрирован в сервисе Логистики. `false` — документ туда не уйдёт. */
  is_logistics: boolean;
  roles: string[] | null;
  /** `operator` — ответ оператора, `manual` — ввод руками. */
  source: string;
  is_active: boolean;
  synced_at: string | null;
  /**
   * Контакты и адрес организации: формат ЭТрН требует их у каждой стороны
   * документа, а оператор отдаёт только наименование и ИНН.
   */
  contact_phone: string | null;
  contact_email: string | null;
  address: LogistPostalAddress | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/** Адрес по элементам формата ЭТрН. Пустые части не передаются. */
export interface LogistPostalAddress {
  postal_code?: string | null;
  region_code?: string | null;
  district?: string | null;
  city?: string | null;
  village?: string | null;
  street?: string | null;
  house?: string | null;
  block?: string | null;
  flat?: string | null;
}

/** Заказ на перевозку (§8.1). Плановая масса — центральный атрибут (BR-12). */
export interface LogistOrder {
  id: string;
  source_system: string;
  source_order_id: string | null;
  shipper_name: string | null;
  consignee_name: string | null;
  carrier_name: string | null;
  /**
   * ИНН сторон. Наименования для выпуска ЭТрН недостаточно: формат
   * идентифицирует участников ящиком ЭДО, а ящик ищется по ИНН в справочнике
   * `logist_edo_boxes`.
   */
  shipper_inn: string | null;
  consignee_inn: string | null;
  carrier_inn: string | null;
  loading_address: string | null;
  unloading_address: string | null;
  /** Те же адреса по элементам: текстовый формат принимает лишь комментарием. */
  loading_address_details: LogistPostalAddress | null;
  unloading_address_details: LogistPostalAddress | null;
  loading_date: string | null;
  /** Номер и дата заказа — реквизиты титула, отдельно от ключа импорта. */
  order_number: string | null;
  order_date: string | null;
  /** Указания грузоотправителя по формату — отдельно от примечания. */
  transportation_directives: string | null;
  cargo_name: string | null;
  planned_mass_kg: number | null;
  planned_volume_m3: number | null;
  segment: string | null;
  status: LogistOrderStatus;
  notes: string | null;
  project_id?: string | null;
  contract_id?: string | null;
  mto_order_id?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Статусы заказа (§7.4) — зеркало Rust ORDER_STATUSES. */
export type LogistOrderStatus =
  | 'new'
  | 'incomplete'
  | 'ready'
  | 'in_transit'
  | 'delivered'
  | 'exception'
  | 'closed';

/** Русские подписи статусов для UI. */
export const LOGIST_ORDER_STATUS_LABELS: Record<LogistOrderStatus, string> = {
  new: 'Новый',
  incomplete: 'Неполный',
  ready: 'Готов',
  in_transit: 'В перевозке',
  delivered: 'Доставлен',
  exception: 'Исключение',
  closed: 'Закрыт',
};

/** Сегменты перевозки (§1.2, Л-02) — определяют обязательность ЭТрН. */
export const LOGIST_SEGMENT_LABELS: Record<string, string> = {
  a: 'A · собственные нужды (ЭТрН не нужна)',
  b: 'B · поставка с доставкой своим транспортом',
  c: 'C · наёмный перевозчик',
  d: 'D · САТКОМ-перевозчик',
  e: 'E · входящая поставка',
};

/** Серия рейсов — шаблон конвейера ходок (§7.2). */
export interface LogistTripSeries {
  id: string;
  order_id: string;
  driver_id: string | null;
  vehicle_id: string | null;
  trailer_id: string | null;
  cargo_name: string | null;
  loading_address: string | null;
  unloading_address: string | null;
  planned_mass_per_trip_kg: number | null;
  planned_trips_count: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Рейс (ходка). Фактическая масса заполняется весовым контролем (§8.4). */
export interface LogistTrip {
  id: string;
  order_id: string;
  series_id: string | null;
  seq_no: number | null;
  driver_id: string | null;
  vehicle_id: string | null;
  trailer_id: string | null;
  cargo_name: string | null;
  loading_address: string | null;
  unloading_address: string | null;
  planned_mass_kg: number | null;
  actual_mass_kg: number | null;
  spec_permit: boolean;
  /** Вердикт весового контроля (§8.4) — зеркало Rust WEIGHT_CHECKS. */
  weight_check: LogistWeightCheck;
  /** Явный лимит массы груза, кг (спецразрешение/override). */
  max_permitted_mass_kg: number | null;
  /** Перегруз, кг (>0 при failed). */
  overload_kg: number | null;
  weight_check_at: string | null;
  /**
   * Метрологический контур (§8.4): можно ли верить самому измерению.
   * `unverified` — взвесили, но подтвердить нечем (нет весов либо истекла
   * поверка). Не блокирует выпуск, но помечает документ.
   */
  weight_metrology: LogistMetrologyCheck;
  /** Осевой контур (TC-L9). Данных по осям чаще всего нет — отсюда свой вердикт. */
  weight_axle: LogistWeightCheck;
  axle_overload_kg: number | null;
  /** Административный риск по ст. 12.21.1 КоАП — информирует, не блокирует. */
  weight_risk: LogistWeightRisk;
  /** Часть статьи 12.21.1 (1–4); null — состава нет. */
  weight_koap_part: number | null;
  /** Провенанс вердикта: проценты, номер перегруженной оси, причины пометки. */
  weight_details: {
    overload_pct?: number | null;
    axle_overload_pct?: number | null;
    axle_worst_index?: number | null;
    metrology_notes?: string[];
    limit_kg?: number | null;
  };
  /**
   * Время работы машины на площадке погрузки. Формат ЭТрН требует все три
   * отметки: заявленную подачу и фактические прибытие и убытие.
   */
  stated_arrival_at: string | null;
  actual_arrival_at: string | null;
  actual_departure_at: string | null;
  /** Характеристики груза для документа. */
  cargo_condition: string | null;
  cargo_package_method: string | null;
  /** Вид тары — двузначный код классификатора («00» — тары нет), не текст. */
  cargo_container_type: string | null;
  cargo_marks: string | null;
  /** Состояние груза, упаковки и кузова при приёме груза перевозчиком. */
  actual_cargo_condition: string | null;
  actual_package_condition: string | null;
  actual_vehicle_body_condition: string | null;
  status: LogistTripStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Вид тары для накладной: код классификатора видов груза и упаковки
 * (приложение 12 к решению Комиссии Таможенного союза № 378). Перечень отдаёт
 * бэкенд — коды буквенные и неочевидные, копия на фронте разошлась бы.
 */
export interface LogistContainerType {
  code: string;
  label: string;
  /** Для каких грузов подходит. */
  hint: string;
}

/** Сведения о погрузке, которых требует формат ЭТрН. */
export interface LogistTripLoadingDetails {
  stated_arrival_at?: string | null;
  actual_arrival_at?: string | null;
  actual_departure_at?: string | null;
  cargo_condition?: string | null;
  cargo_package_method?: string | null;
  cargo_container_type?: string | null;
  cargo_marks?: string | null;
  actual_cargo_condition?: string | null;
  actual_package_condition?: string | null;
  actual_vehicle_body_condition?: string | null;
}

/** Вердикт весового контроля (§8.4). */
export type LogistWeightCheck = 'not_checked' | 'passed' | 'failed';

/** Русские подписи вердикта весового контроля. */
export const LOGIST_WEIGHT_CHECK_LABELS: Record<LogistWeightCheck, string> = {
  not_checked: 'Не проверен',
  passed: 'В норме',
  failed: 'Перегруз',
};

/** Метрологический контур — зеркало Rust METROLOGY_CHECKS. */
export type LogistMetrologyCheck = 'not_checked' | 'ok' | 'warning' | 'unverified';

export const LOGIST_METROLOGY_LABELS: Record<LogistMetrologyCheck, string> = {
  not_checked: 'Не взвешивали',
  ok: 'Измерение подтверждено',
  warning: 'Есть замечания к измерению',
  unverified: 'Весы не подтверждены',
};

/** Административный риск — зеркало Rust RISK_LEVELS. */
export type LogistWeightRisk = 'none' | 'low' | 'medium' | 'high' | 'critical';

/**
 * Уровни риска по ст. 12.21.1 КоАП. Суммы штрафов намеренно не приводим: они
 * меняются чаще порогов, и устаревшая цифра хуже её отсутствия.
 */
export const LOGIST_WEIGHT_RISK_LABELS: Record<LogistWeightRisk, string> = {
  none: 'Штрафа не будет',
  low: 'Превышение до 10%',
  medium: 'Превышение 10–20%',
  high: 'Превышение 20–50%',
  critical: 'Превышение свыше 50%',
};

/** Источники записи взвешивания — зеркало Rust WEIGHING_SOURCES. */
export type LogistWeighingSource = 'scale' | 'manual' | 'ttn';

/** Русские подписи источников взвешивания. */
export const LOGIST_WEIGHING_SOURCE_LABELS: Record<LogistWeighingSource, string> = {
  scale: 'Весы',
  manual: 'Вручную',
  ttn: 'По ТТН',
};

/** Запись взвешивания рейса (§8.4). Нетто — основа вердикта. */
export interface LogistTripWeighing {
  id: string;
  trip_id: string;
  gross_mass_kg: number | null;
  tare_mass_kg: number | null;
  net_mass_kg: number;
  source: LogistWeighingSource;
  weighed_at: string;
  confirmed: boolean;
  note: string | null;
  /**
   * Сведения о весах хранятся рядом с измерением, а не с рейсом: записи
   * взвешивания неизменяемы, и вердикт должен восстанавливать картину на
   * момент подписи.
   */
  scale_number: string | null;
  scale_verified_until: string | null;
  scale_error_pct: number | null;
  /** Нагрузки по осям, кг — в порядке осей автопоезда. */
  axle_loads_kg: number[] | null;
  created_by: string | null;
  created_at: string;
}

/** Статусы рейса (§7.4) — зеркало Rust TRIP_STATUSES. */
export type LogistTripStatus =
  | 'planned'
  | 'assigned'
  | 'loading'
  | 'loaded'
  | 'in_transit'
  | 'unloading'
  | 'attempt_done'
  | 'held_overload';

/** Русские подписи статусов рейса. */
export const LOGIST_TRIP_STATUS_LABELS: Record<LogistTripStatus, string> = {
  planned: 'Запланирован',
  assigned: 'Назначен',
  loading: 'На погрузке',
  loaded: 'Груз принят',
  in_transit: 'В пути',
  unloading: 'На выгрузке',
  attempt_done: 'Попытка завершена',
  held_overload: 'Задержан: перегруз',
};

/**
 * ЭТрН — электронная транспортная накладная в реестре (§7.4, §1.3). Зеркалит
 * Rust `osnova_storage::logist_etrn::LogistEtrn`. РАЗДЕЛЬНО: `state` — наш
 * оркестрационный статус отправки; `operator_status` — юр-статус ДОСЛОВНО от
 * оператора (свободный текст, не наш enum; NULL пока оператор не ответил).
 */
export interface LogistEtrn {
  id: string;
  trip_id: string;
  idempotency_key: string;
  operator_kind: string;
  external_id: string | null;
  title_number: string | null;
  state: LogistEtrnState;
  operator_status: string | null;
  last_error: string | null;
  payload: Record<string, unknown> | null;
  receipt: Record<string, unknown> | null;
  submitted_at: string | null;
  status_checked_at: string | null;
  /** Число неуспешных попыток отправки (backoff фонового воркера). */
  attempts: number;
  /** Не раньше какого момента воркер повторит отправку (NULL — можно сразу). */
  next_retry_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Оркестрационный статус отправки ЭТрН — зеркало Rust ETRN_STATES. */
export type LogistEtrnState = 'draft' | 'submitting' | 'submitted' | 'error' | 'revoked';

/** Русские подписи оркестрационного статуса ЭТрН. */
export const LOGIST_ETRN_STATE_LABELS: Record<LogistEtrnState, string> = {
  draft: 'Черновик',
  submitting: 'Отправка',
  submitted: 'Отправлен',
  error: 'Ошибка',
  revoked: 'Аннулирован',
};

/**
 * Входящий ЭТрН — идемпотентный inbox (Волна 2). Зеркалит Rust
 * `osnova_storage::logist_etrn_inbox::LogistEtrnInbox`. Юр-документ хранит
 * оператор; здесь — снимок полей для связки с поставкой МТО / приёмкой Прораба.
 */
export interface LogistEtrnInbox {
  id: string;
  external_id: string;
  operator_kind: string;
  doc_number: string | null;
  shipper_name: string | null;
  carrier_name: string | null;
  cargo_name: string | null;
  net_mass_kg: number | null;
  loading_address: string | null;
  unloading_address: string | null;
  doc_date: string | null;
  operator_status: string | null;
  status: LogistInboxStatus;
  linked_kind: string | null;
  linked_id: string | null;
  raw: Record<string, unknown> | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

/** Статус обработки входящего ЭТрН — зеркало Rust INBOX_STATUSES. */
export type LogistInboxStatus = 'received' | 'linked' | 'skipped' | 'failed';

/** Русские подписи статусов входящего inbox. */
export const LOGIST_INBOX_STATUS_LABELS: Record<LogistInboxStatus, string> = {
  received: 'Принят',
  linked: 'Связан',
  skipped: 'Пропущен',
  failed: 'Ошибка',
};

/** Кандидат авто-матчинга входящего ЭТрН (детерминированный, §0.2). Зеркалит
 *  Rust `logist_matcher::ScoredCandidate`. `kind` ∈ mto_supply|field_acceptance. */
export interface LogistMatchCandidate {
  kind: string;
  id: string;
  doc_number: string | null;
  supplier: string | null;
  nomenclature: string | null;
  score: number;
  strong: boolean;
  reason: string;
}

/**
 * Черновик заказа, извлечённый LLM из свободного текста письма (Волна 2, §8.1).
 * Зеркалит Rust `logist_llm::OrderDraft`. Масса — кг, дата — ISO YYYY-MM-DD
 * (посчитаны/распарсены детерминированно Rust'ом, §0.2). Все поля опциональны.
 */
export interface LogistOrderDraft {
  shipper_name: string | null;
  consignee_name: string | null;
  carrier_name: string | null;
  loading_address: string | null;
  unloading_address: string | null;
  loading_date: string | null;
  cargo_name: string | null;
  planned_mass_kg: number | null;
  planned_volume_m3: number | null;
  notes: string | null;
}

/** Результат экстракции черновика заказа. `needs_review` всегда true (HITL). */
export interface LogistOrderExtract {
  draft: LogistOrderDraft;
  warnings: string[];
  needs_review: boolean;
  prompt_version: string;
}

/** Русские подписи вида связки. */
export const LOGIST_LINK_KIND_LABELS: Record<string, string> = {
  mto_supply: 'Поставка МТО',
  field_acceptance: 'Приёмка Прораба',
};

/** Подписи справочников для UI-навигации. */
export const LOGIST_DICTIONARIES: Record<LogistDictName, { title: string; subtitle: string }> = {
  drivers: {
    title: 'Водители',
    subtitle:
      'ФИО, ВУ, телефон, привязка к перевозчику. Статус регистрации у оператора ИС ЭПД и наличие ПЭП/УНЭП — предпосылка подписи Т2 водителем.',
  },
  vehicles: {
    title: 'Транспортные средства',
    subtitle:
      'Госномер, марка, СТС, разрешённая максимальная масса, масса без нагрузки, грузоподъёмность — вход весового контроля.',
  },
  trailers: {
    title: 'Прицепы',
    subtitle:
      'Госномер, марка, СТС и массо-габаритные атрибуты прицепов для конфигурации автопоезда в весовом контроле.',
  },
};

/**
 * Карточка подписанта титулов ЭТрН (§4.2, миграция 0228).
 *
 * Формат ФНС требует в документе ФИО, должность и основание полномочий, а для
 * доверенности — её реквизиты. Подписантов несколько и у каждого своя
 * доверенность со своим сроком, поэтому это справочник, а не настройка сервера.
 */
export interface LogistSigner {
  id: string;
  user_id: string;
  full_name: string;
  job_title: string | null;
  status: string;
  poa_id: string | null;
  poa_date: string | null;
  poa_number: string | null;
  poa_valid_until: string | null;
  is_active: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Основание полномочий — классификатор оператора ИС ЭПД (приходит с бэка). */
export interface LogistSignerStatus {
  code: string;
  label: string;
  requires_power_of_attorney: boolean;
}
